import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/Logo';
import { KeyRound, ArrowLeft, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Step 2 — user enters the 6-digit OTP. On success we get an opaque reset
 * token from the backend, stash it in sessionStorage, and move to the
 * new-password page.
 */
export default function VerifyOTPPage() {
  const nav = useNavigate();
  const email = sessionStorage.getItem('32d.reset.email') || '';
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!email) {
      // No email captured — kick back to step 1.
      nav('/forgot-password', { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [email, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await api.post('/auth/verify-otp', { email, otp });
      const token = r.data?.reset_token;
      if (!token) throw new Error('No reset token returned');
      sessionStorage.setItem('32d.reset.token', token);
      nav('/reset-password');
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('A new code was sent.');
    } catch (e) {
      toast.error(parseErr(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r"
           style={{ background: 'hsl(var(--surface-1))', borderColor: 'hsl(var(--border))' }}>
        <Logo variant="horizontal" surface="light" height={44} className="max-w-[280px]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Check your inbox.
          </h1>
          <p className="mt-4 text-[hsl(var(--foreground-muted))] max-w-md text-[15px] leading-relaxed">
            We&rsquo;ve sent a 6-digit code to <strong className="font-mono">{email}</strong>.
            Codes expire in 10 minutes and can only be used once.
          </p>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-subtle))]">
          Didn&rsquo;t get it? Check spam, then <button onClick={resend} className="underline">resend</button>.
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="verify-otp-form">
          <div className="lg:hidden flex justify-center mb-2">
            <Logo variant="stacked" surface="light" height={90} />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <KeyRound size={22} />Enter your code
            </h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))]">
              Emailed to <span className="font-mono">{email}</span>
            </p>
          </div>

          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">6-digit code</span>
            <Input data-testid="verify-otp-input"
                   ref={inputRef}
                   required
                   inputMode="numeric"
                   pattern="[0-9]{6}"
                   maxLength={6}
                   value={otp}
                   onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                   placeholder="123456"
                   className="mt-1 tracking-[8px] text-center text-lg font-mono"
                   autoComplete="one-time-code" />
          </label>

          {error && (
            <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border"
                 style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
                 role="alert" data-testid="verify-otp-error">
              {error}
            </div>
          )}

          <Button data-testid="verify-otp-submit"
                  disabled={busy || otp.length !== 6}
                  type="submit" className="w-full">
            {busy ? 'Verifying…' : 'Verify code'}
          </Button>

          <div className="flex items-center justify-between text-[12.5px] text-[hsl(var(--foreground-muted))]">
            <Link className="inline-flex items-center gap-1 underline" to="/forgot-password" data-testid="verify-otp-back">
              <ArrowLeft size={13} />Use a different email
            </Link>
            <button type="button" onClick={resend} disabled={resending}
                    className="inline-flex items-center gap-1 underline disabled:opacity-60"
                    data-testid="verify-otp-resend">
              <RefreshCcw size={13} />{resending ? 'Resending…' : 'Resend code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
